/**
 * Mock partner Home (`GET /home`) for «سارا محمدی». Amounts are Rial strings, exactly as the API
 * sends them; deterministic so screenshots stay stable between reloads.
 */
import type { DailySeriesPoint, PartnerHomeResponse, SuggestedActionView } from "@mfa-aff/shared";
import { formatJalali, jalaliParts } from "@mfa-aff/ui";
import { levelCardMock } from "./levels";
import { meMock } from "./me";
import { allCommissionsMock, recentCommissionsMock } from "./orders";

const DAY = 86_400_000;
const now = Date.now();
const daysAgo = (d: number, h = 10) => new Date(now - d * DAY + (h - 12) * 3_600_000).toISOString();
const tehranDay = (t: number) => new Date(t + 3.5 * 3_600_000).toISOString().slice(0, 10);

/** Deterministic pseudo-random so screenshots stay stable between reloads. */
function seeded(seed: number) {
    let s = seed;
    return () => {
        s = (s * 1_103_515_245 + 12_345) % 2_147_483_648;
        return s / 2_147_483_648;
    };
}

/** 90 Tehran days, oldest first (toman values rounded to 1,000, sent as rial). */
const series: DailySeriesPoint[] = (() => {
    const rnd = seeded(7);
    const out: DailySeriesPoint[] = [];
    for (let d = 89; d >= 0; d--) {
        const trend = 1 + (89 - d) / 120;
        const weekday = new Date(now - d * DAY).getDay();
        const weekend = weekday === 5 ? 0.45 : weekday === 4 ? 0.8 : 1;
        const spike = rnd() > 0.9 ? 2.4 : 1;
        const newToman = Math.round(((rnd() * 520_000 + 60_000) * trend * weekend * spike) / 1000) * 1000;
        const renewalToman = Math.round(((rnd() * 120_000 + 40_000) * trend) / 1000) * 1000;
        // The first weeks of the window are before the cutover: they come from the old system's history.
        const old = d >= 21;
        out.push({
            date: tehranDay(now - d * DAY),
            newRial: old ? "0" : String(newToman * 10),
            renewalRial: old ? "0" : String(renewalToman * 10),
            legacyNewRial: old ? String(newToman * 10) : "0",
            legacyRenewalRial: old ? String(renewalToman * 10) : "0",
        });
    }
    return out;
})();

const sumRial = (rows: DailySeriesPoint[]) =>
    rows.reduce((s, r) => s + BigInt(r.newRial) + BigInt(r.renewalRial) + BigInt(r.legacyNewRial) + BigInt(r.legacyRenewalRial), 0n).toString();

function suggested(): SuggestedActionView {
    const a = meMock.affiliate;
    if (!a.hasSheba) {
        return {
            key: "add_sheba",
            title: "شبای خود را ثبت کنید",
            description:
                "برای اولین برداشت به حساب بانکی، شبای حسابی که به نام خودتان است لازم است. ثبتش دو دقیقه طول می‌کشد و بعد از آن برداشت بی‌معطلی انجام می‌شود.",
            cta: { label: "ثبت شبا", href: "/wallet" },
        };
    }
    return {
        key: "share_kit",
        title: "یک لینک تازه بسازید",
        description: "برای محصولی که بیشتر از همه معرفی می‌کنید لینک اختصاصی بسازید تا هر خرید از آن به نام شما ثبت شود.",
        cta: { label: "ساخت لینک", href: "/links" },
    };
}

export function partnerHomeMock(): PartnerHomeResponse {
    const a = meMock.affiliate;
    const commissions = allCommissionsMock();
    const pending = commissions.filter((c) => c.status === "pending");
    const held = commissions.filter((c) => c.status === "held");
    const soonest = pending
        .map((c) => c.timeline.eligibleAt!)
        .sort()
        .at(0);
    const sameDay = soonest ? pending.filter((c) => tehranDay(new Date(c.timeline.eligibleAt!).getTime()) === tehranDay(new Date(soonest).getTime())) : [];
    const sum = (rows: typeof commissions) => rows.reduce((s, c) => s + BigInt(c.amountRial), 0n).toString();

    const { year, month } = jalaliParts(new Date());
    const monthStart = series.findIndex((p) => jalaliParts(new Date(`${p.date}T08:30:00Z`)).month === month && jalaliParts(new Date(`${p.date}T08:30:00Z`)).year === year);
    const monthRows = monthStart >= 0 ? series.slice(monthStart) : [];
    const newRial = monthRows.reduce((s, r) => s + BigInt(r.newRial), 0n);
    const renewalRial = monthRows.reduce((s, r) => s + BigInt(r.renewalRial), 0n);

    return {
        partner: { id: a.id, displayName: a.displayName, refCode: a.refCode, type: a.type, hasSheba: a.hasSheba, kycStatus: a.kycStatus },
        balances: {
            availableRial: "184500000",
            withdrawableRial: "184500000",
            pendingRial: sum(pending),
            pendingCount: pending.length,
            nextRelease: soonest
                ? { at: soonest, atJalali: formatJalali(soonest), amountRial: sum(sameDay), count: sameDay.length }
                : null,
            reservedRial: "0",
            paidToDateRial: "1423800000",
            payoutCount: 14,
            firstPayoutAt: daysAgo(510),
            lastPayout: { at: daysAgo(9), amountRial: "82000000", channel: "wallet" },
            heldRial: sum(held),
            heldCount: held.length,
        },
        level: levelCardMock(),
        thisMonth: {
            from: monthRows[0] ? `${monthRows[0].date}T00:00:00.000Z` : new Date().toISOString(),
            to: new Date().toISOString(),
            label: formatJalali(new Date(), "monthYear"),
            clicks: 412,
            orders: 9,
            newSales: 7,
            renewals: 3,
            commissionNewRial: newRial.toString(),
            commissionRenewalRial: renewalRial.toString(),
            commissionTotalRial: (newRial + renewalRial).toString(),
            source: "daily_stats",
        },
        suggestedAction: suggested(),
        series: { days: structuredClone(series), totals: { d30: sumRial(series.slice(-30)), d90: sumRial(series) }, source: "daily_stats" },
        recent: recentCommissionsMock(5),
        lifetime: { totalRial: "1386400000", platformRial: "248600000", legacyRial: "1137800000" },
        legacy: {
            joinedAt: daysAgo(2210),
            commissionRial: "1137800000",
            commissionRows: 46,
            firstAt: daysAgo(1162),
            lastAt: daysAgo(380),
            visitors: 5441,
            visitsByMonth: ["مهر", "آبان", "آذر", "دی", "بهمن", "اسفند", "فروردین", "اردیبهشت", "خرداد", "تیر", "مرداد", "شهریور"].map((m, i) => ({
                month: `2025-${String(((i + 9) % 12) + 1).padStart(2, "0")}`,
                label: `${m} ${i < 6 ? "۱۴۰۴" : "۱۴۰۵"}`,
                clicks: 120 + ((i * 37) % 90),
            })),
            payoutCount: 3,
            payoutRial: "21450000",
        },
    };
}
